import { typographyTokens } from './typography-tokens'

export type FigmaTypeStepName = 'D1' | 'D2' | 'D3' | 'D4' | 'H1' | 'P1' | 'P2' | 'P3'

export type FigmaTypeStep = {
    step: FigmaTypeStepName
    size: number
    lineHeight: number
    leading: number
}

export const FIGMA_TYPE_STEPS: FigmaTypeStepName[] = [
    'D1',
    'D2',
    'D3',
    'D4',
    'H1',
    'P1',
    'P2',
    'P3',
]

type TypographyToken = { value?: unknown }

/**
 * Figma renames the casing and spacing of these groups from time to time
 * (`Line Height/D1` became `line-height/d1` in the 2026-08-27 pass), so look
 * tokens up on a normalised key rather than the literal one.
 */
const BY_NORMALISED_KEY: Record<string, TypographyToken> = Object.fromEntries(
    Object.entries(typographyTokens as Record<string, TypographyToken>).map(([alias, token]) => [
        alias.toLowerCase().replace(/[^a-z0-9]+/g, ''),
        token,
    ]),
)

const numberOf = (alias: string): number => {
    const token = BY_NORMALISED_KEY[alias.toLowerCase().replace(/[^a-z0-9]+/g, '')]
    if (typeof token?.value !== 'number') {
        throw new Error(`figma typography: "${alias}" is missing or not a number`)
    }
    return token.value
}

const round = (value: number, places: number): number => Number(value.toFixed(places))

export const figmaTypeScale: Record<FigmaTypeStepName, FigmaTypeStep> = Object.fromEntries(
    FIGMA_TYPE_STEPS.map(step => {
        const size = round(numberOf(`Size/${step}`), 2)
        const lineHeight = round(numberOf(`Line Height/${step}`), 2)
        return [step, { step, size, lineHeight, leading: round(lineHeight / size, 2) }]
    }),
) as Record<FigmaTypeStepName, FigmaTypeStep>

export const FIGMA_FONT_WEIGHTS = ['Regular', 'Medium', 'SemiBold'] as const

export const FIGMA_FONT_FAMILY = String(BY_NORMALISED_KEY['familyfamily']?.value ?? '').trim()

/**
 * The size a call site asks for, named after the Figma variable it renders.
 * A design says "P2"; the code writes `size="P2"`. No translation step, so
 * nothing to get wrong.
 */
export type FigmaTextSize = FigmaTypeStepName

/**
 * The original prop values, coined before the ramp was tied to Figma. Still
 * accepted so the ~990 existing call sites keep working, and each resolves to
 * exactly the same class as its Figma name.
 *
 * @deprecated Use the Figma step — `D1`…`D4`, `H1`, `P1`…`P3`. Beware while
 * migrating: `h1` is Figma's **D2** (36.49px), not `H1` (18px), and `heading`
 * is Figma's `H1`.
 */
export type LegacyTextSize =
    | 'display'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'heading'
    | 'body-lg'
    | 'body'
    | 'caption'

export type TextSize = FigmaTextSize | LegacyTextSize

/** @deprecated Mapping kept for the migration off {@link LegacyTextSize}. */
export const LEGACY_SIZE_TO_FIGMA_STEP: Record<LegacyTextSize, FigmaTypeStepName> = {
    'display': 'D1',
    'h1': 'D2',
    'h2': 'D3',
    'h3': 'D4',
    'heading': 'H1',
    'body-lg': 'P1',
    'body': 'P2',
    'caption': 'P3',
}

const isLegacy = (size: TextSize): size is LegacyTextSize => size in LEGACY_SIZE_TO_FIGMA_STEP

export const figmaStepNameFor = (size: TextSize): FigmaTypeStepName =>
    isLegacy(size) ? LEGACY_SIZE_TO_FIGMA_STEP[size] : size

export const figmaStepFor = (size: TextSize): FigmaTypeStep =>
    figmaTypeScale[figmaStepNameFor(size)]

/**
 * The Tailwind classes each Figma step emits.
 *
 * These have to be literal strings: Tailwind's JIT only generates a class it
 * can find verbatim in scanned source, so `text-[${size}px]` built at runtime
 * would produce a class that does not exist and text would silently fall back
 * to the inherited size. Named theme keys (`text-p2`) would avoid the literals
 * but collide with text colour in tailwind-merge — the size loses — so they
 * need a configured merger first.
 *
 * `figma-typography.spec.ts` asserts every value here against `figmaTypeScale`,
 * so the literals cannot drift from Figma unnoticed.
 */
export const FIGMA_STEP_CLASSES: Record<FigmaTypeStepName, string> = {
    D1: 'text-[3.25rem] leading-[123%]',
    D2: 'text-[2.25rem] leading-[122%]',
    D3: 'text-[2rem] leading-[125%]',
    D4: 'text-[1.438rem] leading-[122%]',
    H1: 'text-[1.125rem] leading-[133%]',
    P1: 'text-[1rem] leading-[150%]',
    P2: 'text-[0.875rem] leading-[143%]',
    P3: 'text-[0.813rem] leading-[154%]',
}

/**
 * Every accepted `size` value, Figma-named and legacy alike, pointing at the
 * same eight class strings. Tailwind only needs each literal to appear once in
 * scanned source, so the derived half costs nothing.
 */
export const TEXT_SIZE_CLASSES: Record<TextSize, string> = {
    ...FIGMA_STEP_CLASSES,
    ...(Object.fromEntries(
        (Object.keys(LEGACY_SIZE_TO_FIGMA_STEP) as LegacyTextSize[]).map(legacy => [
            legacy,
            FIGMA_STEP_CLASSES[LEGACY_SIZE_TO_FIGMA_STEP[legacy]],
        ]),
    ) as Record<LegacyTextSize, string>),
}
